// The volume to show: confirmed count once set, forecast until then. Never `actual || forecast`,
// since a confirmed zero is real. Weight is deliberately not aggregated.

export interface StopVolumeInput {
  volumeForecast?: number | null
  volumeActual?: number | null
  loadingUnitsForecast?: number | null
  loadingUnitsActual?: number | null
  loadingUnitType?: string | null
}

/** `partial` only ever describes a set of stops, never one stop. */
export type VolumeSource = "actual" | "partial" | "forecast"

export interface ReportedStopVolume {
  volume: number
  /** Null when neither figure is set. */
  loadingUnits: number | null
  unitType: string | null
  isActual: boolean
}

/** Depot injections and breaks carry no volume. */
export function carriesVolume(stop: StopVolumeInput): boolean {
  return (stop.volumeForecast ?? 0) > 0 || stop.volumeActual != null
}

export function reportedStopVolume(stop: StopVolumeInput): ReportedStopVolume {
  const hasActual = stop.volumeActual != null
  return {
    volume: hasActual ? (stop.volumeActual as number) : (stop.volumeForecast ?? 0),
    loadingUnits:
      stop.loadingUnitsActual != null
        ? stop.loadingUnitsActual
        : (stop.loadingUnitsForecast ?? null),
    unitType: stop.loadingUnitType ?? null,
    isActual: hasActual,
  }
}

export interface ReportedRouteVolume {
  volume: number
  forecastVolume: number
  loadingUnits: number | null
  /** Null when the stops disagree or none is set. */
  unitType: string | null
  source: VolumeSource
  stopsConfirmed: number
  stopsCarryingVolume: number
}

/** Counts only stops that carry volume, so breaks and depot stops never hold back `actual`. */
export function reportedRouteVolume(stops: StopVolumeInput[]): ReportedRouteVolume {
  const counted = stops.filter(carriesVolume)

  let volume = 0
  let forecastVolume = 0
  let loadingUnits = 0
  let sawLoadingUnits = false
  let stopsConfirmed = 0
  const unitTypes = new Set<string>()

  for (const stop of counted) {
    const reported = reportedStopVolume(stop)
    volume += reported.volume
    forecastVolume += stop.volumeForecast ?? 0
    if (reported.loadingUnits != null) {
      loadingUnits += reported.loadingUnits
      sawLoadingUnits = true
    }
    if (reported.unitType) unitTypes.add(reported.unitType)
    if (reported.isActual) stopsConfirmed++
  }

  const source: VolumeSource =
    stopsConfirmed === 0 ? "forecast" : stopsConfirmed === counted.length ? "actual" : "partial"

  return {
    volume,
    forecastVolume,
    loadingUnits: sawLoadingUnits ? loadingUnits : null,
    unitType: unitTypes.size === 1 ? [...unitTypes][0] : null,
    source,
    stopsConfirmed,
    stopsCarryingVolume: counted.length,
  }
}

export const VOLUME_SOURCE_LABELS: Record<VolumeSource, string> = {
  actual: "Actual",
  partial: "Part actual",
  forecast: "Forecast",
}
